import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = import.meta.env?.VITE_API_URL || 
                process.env.REACT_APP_API_URL || 
                'https://cyberguard-api-ka8f.onrender.com';

const AuthContext = createContext(null);

// Affiché pendant la vérification de session. Le serveur (offre gratuite Render) peut mettre
// près d'une minute à se réveiller : on le dit plutôt que de laisser un écran noir.
function StartupScreen() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ minHeight: '100vh', background: '#09090b', color: '#a1a1aa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, fontFamily: 'monospace', padding: 24, textAlign: 'center' }}>
      <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Chargement…</p>
      {slow && <p style={{ fontSize: 13, maxWidth: 360 }}>Le serveur se réveille, cela peut prendre jusqu'à une minute. Merci de patienter.</p>}
    </div>
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (!token || token === 'undefined' || token === 'null') {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Auth check failed:", error.response?.status);
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Ouvre une session avec un jeton et charge le profil complet (avec subscription_plan).
  const startSession = useCallback(async (newToken, fallbackUser) => {
    localStorage.setItem('auth_token', newToken);
    setToken(newToken);
    try {
      const meRes = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` }
      });
      setUser(meRes.data);
      return meRes.data;
    } catch {
      if (fallbackUser) setUser(fallbackUser);
      return fallbackUser;
    }
  }, []);

  // Renvoie { twoFactorRequired: true, challenge } si la double authentification est active.
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      if (response.data.two_factor_required) {
        return { twoFactorRequired: true, challenge: response.data.challenge };
      }
      const { token: newToken, user: userData } = response.data;
      await startSession(newToken, userData);
      return userData;
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Erreur de connexion");
    }
  };

  const completeTwoFactor = async (challenge, code) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/2fa`, { challenge, code });
      const { token: newToken, user: userData } = response.data;
      await startSession(newToken, userData);
      return userData;
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Code incorrect");
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email, password, name
      });
      const { token: newToken, user: userData } = response.data;
      await startSession(newToken, userData);
      return userData;
    } catch (error) {
      throw new Error(error.response?.data?.detail || "Erreur lors de l'inscription");
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    token,
    login,
    completeTwoFactor,
    startSession,
    register,
    logout,
    updateUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <StartupScreen /> : children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
